import React from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from 'use-toast-mui';
import { Box } from '@mui/material';
import CommaInput from './CommaInput';
import {
  Card,
  SectionTitle,
  SectionContent,
  FormRow,
  FormFieldGroup,
  FormFieldLabel,
  FormFieldDesc,
  Divider,
  ToggleSwitch,
  ToggleFieldRow,
  FieldBlock,
  InputUnit,
  RateButtonGroup,
  RateButton,
  OvertimeRow,
  HelpTooltip,
  NoteText,
} from './SalarySettingStyled';

const RATE_OPTIONS = ['100%', '150%', '200%'];

// 백엔드(resolveHourlyAllowance)는 "원 지급" 입력값이 있으면 그 금액을 시간당 지급액으로
// 그대로 쓰고, 비어 있을 때만 요율로 계산한다 — 추가로 더하는 값이 아니라 대체값이다.
// 그래서 요율 버튼을 고르면(토글을 처음 켤 때도 마찬가지) 그 순간 계산되는 금액을 이
// 입력에 채워서, 화면에 보이는 값과 실제 저장되는 값이 항상 일치하게 한다. 값을 0으로
// 비우면 다시 "요율로 자동 계산" 모드가 된다.
const OvertimeSettingItem = ({
  title,
  desc,
  enabled,
  onToggle,
  selectedRate,
  onRateChange,
  extraValue,
  onExtraChange,
  baseAmount,
  salaryType,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const rateNum = parseInt((selectedRate || '150%').replace('%', ''), 10);
  const hourlyRate = salaryType === 'HOURLY' ? (baseAmount || 0) : (baseAmount > 0 ? Math.floor(baseAmount / 209) : 0);
  const computedAmount = Math.floor(hourlyRate * rateNum / 100);

  const handlePresetClick = rate => {
    onRateChange(rate);
    const num = parseInt(rate.replace('%', ''), 10);
    onExtraChange(Math.floor(hourlyRate * num / 100));
  };

  const handleToggle = () => {
    // 기본 급여액이 없으면 시급(기본급 ÷ 209시간)을 계산할 수 없어 자동 채움이 불가능하다 —
    // 토글을 켜기 전에 먼저 입력하도록 안내한다.
    if (enabled !== 'Y' && !(baseAmount > 0)) {
      toast.error(t('기본 급여액을 먼저 입력해주세요'));
      return;
    }
    // 처음 켤 때(값이 아직 없을 때)는 현재 선택된 요율(기본 150%) 기준 계산값을 미리 채워준다.
    if (enabled !== 'Y' && !extraValue && hourlyRate > 0) {
      onExtraChange(computedAmount);
    }
    onToggle();
  };

  return (
    <FieldBlock>
      <FormRow sx={{ alignItems: 'flex-start' }}>
        <FormFieldGroup>
          <ToggleFieldRow>
            <FormFieldLabel>{title}</FormFieldLabel>
            <ToggleSwitch checked={enabled === 'Y'} onChange={handleToggle} />
          </ToggleFieldRow>
          <FormFieldDesc>{desc}</FormFieldDesc>
        </FormFieldGroup>

        {enabled === 'Y' && (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <OvertimeRow>
              <RateButtonGroup>
                {RATE_OPTIONS.map(rate => (
                  <RateButton
                    key={rate}
                    isSelected={selectedRate === rate}
                    onClick={() => handlePresetClick(rate)}
                  >
                    {rate}
                  </RateButton>
                ))}
              </RateButtonGroup>
              <InputUnit>{t('시간당')}</InputUnit>
              <CommaInput
                inputWidth="100px"
                value={extraValue}
                onChange={onExtraChange}
                allowDecimal={false}
              />
              <InputUnit>{t('원 지급')}</InputUnit>
            </OvertimeRow>
            {baseAmount > 0 && (
              <Box sx={{ fontSize: '12px', color: '#888', lineHeight: 1.6 }}>
                시급 <strong style={{ color: '#4AB3BC' }}>{hourlyRate.toLocaleString()}원</strong>
                {salaryType !== 'HOURLY' && ` (기본급 ${baseAmount.toLocaleString()}원 ÷ 209시간)`}
                {' × '}{rateNum}%{' = '}
                <strong style={{ color: '#4AB3BC' }}>{computedAmount.toLocaleString()}원/시간</strong>
              </Box>
            )}
            <NoteText sx={{ fontSize: '12px' }}>
              {t('※ 이 금액이 실제 시간당 지급액입니다. 0으로 비우면 시급 변경 시 자동으로 재계산됩니다.')}
            </NoteText>
          </Box>
        )}
      </FormRow>
    </FieldBlock>
  );
};

const SalarySettingMonthly = ({ settings, onSettingsChange }) => {
  const { t } = useTranslation();

  return (
    <Card>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <SectionTitle>{t('수당 설정')}</SectionTitle>
        <HelpTooltip title={"출퇴근 기록을 기반으로 자동 계산됩니다.\n\n비율(100%/150%/200%): 시급 × 비율 × 해당 시간\n원 지급 입력칸에는 그 시점에 계산된 금액이 채워지며, 이 금액이 실제 시간당 지급액입니다. 0으로 비우면 시급 변경에 따라 자동으로 재계산됩니다.\n\n시급 = 기본급 ÷ 209시간 (월급제 기준)"} />
      </Box>

      <SectionContent sx={{ mt: '24px' }}>
        <OvertimeSettingItem
          title={t('연장근로 수당')}
          desc={t('1일 8시간 초과 근무 시')}
          enabled={settings.overtimeEnabled}
          onToggle={() =>
            onSettingsChange('overtimeEnabled', settings.overtimeEnabled === 'Y' ? 'N' : 'Y')
          }
          selectedRate={settings.overtimeRate ?? '150%'}
          onRateChange={val => onSettingsChange('overtimeRate', val)}
          extraValue={settings.overtimeExtra}
          onExtraChange={val => onSettingsChange('overtimeExtra', val)}
          baseAmount={settings.baseAmount}
          salaryType={settings.salaryType}
        />

        <Divider />

        <OvertimeSettingItem
          title={t('야간근로 수당')}
          desc={t('22:00~06:00 근무 시')}
          enabled={settings.nightWorkEnabled}
          onToggle={() =>
            onSettingsChange('nightWorkEnabled', settings.nightWorkEnabled === 'Y' ? 'N' : 'Y')
          }
          selectedRate={settings.nightWorkRate ?? '150%'}
          onRateChange={val => onSettingsChange('nightWorkRate', val)}
          extraValue={settings.nightWorkExtra}
          onExtraChange={val => onSettingsChange('nightWorkExtra', val)}
          baseAmount={settings.baseAmount}
          salaryType={settings.salaryType}
        />

        <Divider />

        <OvertimeSettingItem
          title={t('휴일근로 수당')}
          desc={t('주말(토/일) 근무 시')}
          enabled={settings.holidayWorkEnabled}
          onToggle={() =>
            onSettingsChange('holidayWorkEnabled', settings.holidayWorkEnabled === 'Y' ? 'N' : 'Y')
          }
          selectedRate={settings.holidayWorkRate ?? '150%'}
          onRateChange={val => onSettingsChange('holidayWorkRate', val)}
          extraValue={settings.holidayWorkExtra}
          onExtraChange={val => onSettingsChange('holidayWorkExtra', val)}
          baseAmount={settings.baseAmount}
          salaryType={settings.salaryType}
        />
      </SectionContent>
    </Card>
  );
};

export default SalarySettingMonthly;
